import { ANYWHERE_API_PATH, anywhereDownload, anywhereRequest } from './client';

/** Multipart do banco de imagens: campos de texto + o arquivo no campo `file`. */
const imageBankFormData = (file, fields = {}) => {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') formData.append(key, String(value));
  });
  formData.append('file', file);
  return formData;
};

/**
 * Build an immutable API facade for one Anywhere scope. The default exports
 * below keep the phase-1 client screens backwards compatible; admin routes use
 * this factory with `/anywhere/admin/clientes/:idClient`.
 */
export const createAnywhereService = (basePath = ANYWHERE_API_PATH) => {
  const get = (path, params) => anywhereRequest({ method: 'get', basePath, path, params });
  const post = (path, data, config) => anywhereRequest({ method: 'post', basePath, path, data, ...config });
  const put = (path, data) => anywhereRequest({ method: 'put', basePath, path, data });
  const patch = (path, data) => anywhereRequest({ method: 'patch', basePath, path, data });
  const remove = (path) => anywhereRequest({ method: 'delete', basePath, path });
  // O CRUD de clientes mora na raiz administrativa. Fixar esta base evita que, quando a mesma
  // fachada estiver sob `/admin/clientes/:idClient`, uma leitura de clientes seja acidentalmente
  // enviada para o cliente atualmente selecionado.
  const adminGet = (path, params) => anywhereRequest({ method: 'get', basePath: ANYWHERE_API_PATH, path, params });
  const adminPost = (path, data) => anywhereRequest({ method: 'post', basePath: ANYWHERE_API_PATH, path, data });
  const adminPut = (path, data) => anywhereRequest({ method: 'put', basePath: ANYWHERE_API_PATH, path, data });
  const adminRemove = (path, data) => anywhereRequest({ method: 'delete', basePath: ANYWHERE_API_PATH, path, data });

  return {
    listAdminClientes: (query) => adminGet('/admin/clientes', query),
    getAdminCliente: (idClient) => adminGet(`/admin/clientes/${idClient}`),
    createAdminCliente: (body) => adminPost('/admin/clientes', body),
    updateAdminCliente: (idClient, body) => adminPut(`/admin/clientes/${idClient}`, body),
    deleteAdminCliente: (idClient) => adminRemove(`/admin/clientes/${idClient}`),
    // Mídias de todos os clientes também moram na raiz: nunca sob o cliente selecionado.
    listAdminMidias: (query) => adminGet('/admin/midias', query),
    getAdminMidia: (idMedia) => adminGet(`/admin/midias/${idMedia}`),
    getAdminMidiaClientes: (idMedia) => adminGet(`/admin/midias/${idMedia}/clientes`),
    shareAdminMidia: (idMedia, body) => adminPost(`/admin/midias/${idMedia}/clientes`, body),
    revokeAdminMidia: (idMedia, body) => adminRemove(`/admin/midias/${idMedia}/clientes`, body),
    trocarMascaraAdminMidia: (idMedia, body) => adminPost(`/admin/midias/${idMedia}/trocar-mascara`, body),
    // Banco de imagens de todos os clientes: mesma raiz administrativa das mídias de todos.
    listAdminBancoImagens: (query) => adminGet('/admin/banco-imagens', query),
    getAdminImagemBanco: (idImage) => adminGet(`/admin/banco-imagens/${idImage}`),
    updateAdminImagemBanco: (idImage, body) => adminPut(`/admin/banco-imagens/${idImage}`, body),
    toggleAdminImagemBancoStatus: (idImage, body) =>
      anywhereRequest({
        method: 'patch',
        basePath: ANYWHERE_API_PATH,
        path: `/admin/banco-imagens/${idImage}/status`,
        data: body,
      }),
    deleteAdminImagemBanco: (idImage) => adminRemove(`/admin/banco-imagens/${idImage}`),
    uploadAdminImagemBanco: ({ file, fields = {}, onUploadProgress }) =>
      anywhereRequest({
        method: 'post',
        basePath: ANYWHERE_API_PATH,
        path: '/admin/banco-imagens',
        data: imageBankFormData(file, fields),
        onUploadProgress,
        timeout: 0,
      }),
    editarAdminImagemBanco: (idImage, body) => adminPost(`/admin/banco-imagens/${idImage}/imagem`, body),
    getCliente: () => get('/cliente'),
    listTerminais: (query) => get('/terminais', query),
    getTerminaisResumo: (query) => get('/terminais/resumo', query),
    exportTerminais: (query) =>
      anywhereDownload({
        basePath,
        path: '/terminais/export',
        params: query,
        fallbackFilename: 'Terminais.xlsx',
      }),
    getTerminal: (idTerminal) => get(`/terminais/${idTerminal}`),
    createTerminal: (body) => post('/terminais', body),
    updateTerminal: (idTerminal, body) => put(`/terminais/${idTerminal}`, body),
    toggleTerminalStatus: (idTerminal, body) => patch(`/terminais/${idTerminal}/status`, body),
    deleteTerminal: (idTerminal) => remove(`/terminais/${idTerminal}`),
    forcarAtualizacaoTerminal: (idTerminal, body) => post(`/terminais/${idTerminal}/forcar-atualizacao`, body),
    solicitarCapturaTerminal: (idTerminal) => post(`/terminais/${idTerminal}/captura`),
    getDiagnosticoTerminal: (idTerminal, query) => get(`/terminais/${idTerminal}/diagnostico`, query),
    getPrevisualizacao: ({ idTerminal, ...query }) => get('/previsualizacao', { idTerminal, ...query }),
    listMidias: (query) => get('/midias', query),
    getMidia: (idMedia) => get(`/midias/${idMedia}`),
    getImpactoMidia: (idMedia) => get(`/midias/${idMedia}/impacto`),
    updateMidia: (idMedia, body) => put(`/midias/${idMedia}`, body),
    toggleMidiaStatus: (idMedia, body) => patch(`/midias/${idMedia}/status`, body),
    deleteMidia: (idMedia) => remove(`/midias/${idMedia}`),
    // Um arquivo por requisição: o back grava um arquivo e uma linha por vez, e o progresso fica por arquivo.
    uploadMidia: ({ file, fields = {}, onUploadProgress }) => {
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') formData.append(key, String(value));
      });
      formData.append('file', file);
      return post('/midias', formData, { onUploadProgress, timeout: 0 });
    },
    // Editor de imagem da mídia: a lista de operações é aplicada de uma vez, no back, com sharp.
    editarImagemMidia: (idMedia, body) => post(`/midias/${idMedia}/imagem`, body),
    listBancoImagens: (query) => get('/banco-imagens', query),
    getImagemBanco: (idImage) => get(`/banco-imagens/${idImage}`),
    updateImagemBanco: (idImage, body) => put(`/banco-imagens/${idImage}`, body),
    toggleImagemBancoStatus: (idImage, body) => patch(`/banco-imagens/${idImage}/status`, body),
    deleteImagemBanco: (idImage) => remove(`/banco-imagens/${idImage}`),
    uploadImagemBanco: ({ file, fields = {}, onUploadProgress }) =>
      post('/banco-imagens', imageBankFormData(file, fields), { onUploadProgress, timeout: 0 }),
    editarImagemBanco: (idImage, body) => post(`/banco-imagens/${idImage}/imagem`, body),
    listProgramacoes: (query) => get('/programacoes', query),
    getProgramacoesResumo: (query) => get('/programacoes/resumo', query),
    getProgramacao: (idSchedule) => get(`/programacoes/${idSchedule}`),
    createProgramacao: (body) => post('/programacoes', body),
    updateProgramacao: (idSchedule, body) => put(`/programacoes/${idSchedule}`, body),
    toggleProgramacaoStatus: (idSchedule, body) => patch(`/programacoes/${idSchedule}/status`, body),
    duplicarProgramacao: (idSchedule, body) => post(`/programacoes/${idSchedule}/duplicar`, body),
    deleteProgramacao: (idSchedule) => remove(`/programacoes/${idSchedule}`),
    getPlaylist: (idSchedule) => get(`/programacoes/${idSchedule}/playlist`),
    addPlaylistItem: (idSchedule, body) => post(`/programacoes/${idSchedule}/playlist`, body),
    replacePlaylist: (idSchedule, body) => put(`/programacoes/${idSchedule}/playlist`, body),
    replacePlaylistItem: (idSchedule, idScheduleMedia, body) =>
      put(`/programacoes/${idSchedule}/playlist/${idScheduleMedia}`, body),
    deletePlaylistItem: (idSchedule, idScheduleMedia) =>
      remove(`/programacoes/${idSchedule}/playlist/${idScheduleMedia}`),
    movePlaylistItemUp: (idSchedule, idScheduleMedia) =>
      post(`/programacoes/${idSchedule}/playlist/${idScheduleMedia}/subir`),
    movePlaylistItemDown: (idSchedule, idScheduleMedia) =>
      post(`/programacoes/${idSchedule}/playlist/${idScheduleMedia}/descer`),
    reorderPlaylistItem: (idSchedule, idScheduleMedia, body) =>
      put(`/programacoes/${idSchedule}/playlist/${idScheduleMedia}/ordem`, body),
    getProgramacaoTerminais: (idSchedule) => get(`/programacoes/${idSchedule}/terminais`),
    addProgramacaoTerminais: (idSchedule, body) => post(`/programacoes/${idSchedule}/terminais`, body),
    addProgramacaoTerminaisPorResolucao: (idSchedule, body) =>
      post(`/programacoes/${idSchedule}/terminais/por-resolucao`, body),
    removeProgramacaoTerminal: (idSchedule, idTerminal) =>
      remove(`/programacoes/${idSchedule}/terminais/${idTerminal}`),
    listMonitoramento: (query) => get('/monitoramento', query),
    getMonitoramentoResumo: (query) => get('/monitoramento/resumo', query),
    listConteudos: (query) => get('/conteudos', query),
    getConteudo: (idContent) => get(`/conteudos/${idContent}`),
    updateConteudo: (idContent, body) => put(`/conteudos/${idContent}`, body),
    toggleConteudoStatus: (idContent, body) => patch(`/conteudos/${idContent}/status`, body),
    listMascarasConteudo: () => get('/conteudos/mascaras'),
    // Com imagem nova vai em multipart (o arquivo no campo `file`); sem ela, JSON.
    createConteudo: ({ body, file, onUploadProgress }) =>
      file
        ? post('/conteudos', imageBankFormData(file, body), { onUploadProgress, timeout: 0 })
        : post('/conteudos', body),
    deleteConteudo: (idContent) => remove(`/conteudos/${idContent}`),
    listMuralMensagens: (query) => get('/mural/mensagens', query),
    listMuralDestinatarios: () => get('/mural/destinatarios'),
    createMuralMensagem: (body) => post('/mural/mensagens', body),
  };
};

/** Default facade used by existing client-scoped screens. */
export const anywhereApi = createAnywhereService();

// Named exports preserve the public phase-1 service API.
export const {
  getCliente,
  listTerminais,
  getTerminaisResumo,
  exportTerminais,
  getTerminal,
  createTerminal,
  updateTerminal,
  toggleTerminalStatus,
  deleteTerminal,
  forcarAtualizacaoTerminal,
  solicitarCapturaTerminal,
  getDiagnosticoTerminal,
  getPrevisualizacao,
  listMidias,
  getMidia,
  getImpactoMidia,
  updateMidia,
  toggleMidiaStatus,
  deleteMidia,
  uploadMidia,
  listProgramacoes,
  getProgramacoesResumo,
  getProgramacao,
  createProgramacao,
  updateProgramacao,
  toggleProgramacaoStatus,
  duplicarProgramacao,
  deleteProgramacao,
  getPlaylist,
  addPlaylistItem,
  replacePlaylist,
  replacePlaylistItem,
  deletePlaylistItem,
  movePlaylistItemUp,
  movePlaylistItemDown,
  reorderPlaylistItem,
  getProgramacaoTerminais,
  addProgramacaoTerminais,
  addProgramacaoTerminaisPorResolucao,
  removeProgramacaoTerminal,
  listMonitoramento,
  getMonitoramentoResumo,
  listConteudos,
  getConteudo,
  updateConteudo,
  toggleConteudoStatus,
  listMascarasConteudo,
  createConteudo,
  deleteConteudo,
  listMuralMensagens,
  listMuralDestinatarios,
  createMuralMensagem,
  listAdminClientes,
  getAdminCliente,
  createAdminCliente,
  updateAdminCliente,
  deleteAdminCliente,
  listAdminMidias,
  getAdminMidia,
  getAdminMidiaClientes,
  shareAdminMidia,
  revokeAdminMidia,
  trocarMascaraAdminMidia,
  listAdminBancoImagens,
  getAdminImagemBanco,
  updateAdminImagemBanco,
  toggleAdminImagemBancoStatus,
  deleteAdminImagemBanco,
  uploadAdminImagemBanco,
  editarAdminImagemBanco,
  editarImagemMidia,
  listBancoImagens,
  getImagemBanco,
  updateImagemBanco,
  toggleImagemBancoStatus,
  deleteImagemBanco,
  uploadImagemBanco,
  editarImagemBanco,
} = anywhereApi;
